import type { GallerySnapshot, ResearchLineageNodeProjection } from "@roboscientist/contracts";
import { parseGallerySnapshot } from "./snapshot.js";

export const publicReadOnly = import.meta.env.VITE_GALLERY_PUBLIC_READONLY === "true";

type PreviewNode = Pick<ResearchLineageNodeProjection, "ref" | "directionPath" | "primaryParentRef" | "secondaryParentRefs"> & {
  latestAssessment: { outcome: NonNullable<ResearchLineageNodeProjection["latestAssessment"]>["outcome"] } | null;
};

export type GalleryStudy = {
  directory: string;
  snapshot: GallerySnapshot["snapshot"];
  lineageNodes: PreviewNode[];
  counts: { evolutionNodes: number; experiments: number; runs: number; reports: number };
  hasPaper: boolean;
};

const directoryName = /^[a-f0-9]{64}$/;
const studyDirectories = new Map<string, string>();

export function studyFromSnapshot(snapshot: GallerySnapshot): GalleryStudy {
  return {
    directory: "",
    snapshot: snapshot.snapshot,
    lineageNodes: snapshot.lineage.nodes.map((node) => ({
      ref: node.ref,
      directionPath: node.directionPath,
      primaryParentRef: node.primaryParentRef,
      secondaryParentRefs: node.secondaryParentRefs,
      latestAssessment: node.latestAssessment ? { outcome: node.latestAssessment.outcome } : null
    })),
    counts: {
      evolutionNodes: snapshot.lineage.nodes.length,
      experiments: snapshot.experiments.length,
      runs: snapshot.runs.length,
      reports: snapshot.reports.length
    },
    hasPaper: snapshot.papers.length > 0
  };
}

const publishedUrl = (path: string): string => `./cdn/${path}`;

export async function listPublishedStudies(): Promise<GalleryStudy[]> {
  const response = await fetch(publishedUrl("index.json"), { cache: "no-store" });
  if (!response.ok) throw new Error(`Published Gallery index returned HTTP ${response.status}.`);
  const payload = await response.json() as { schema?: unknown; studies?: unknown };
  if (payload.schema !== "simate.gallery.index.v1" || !Array.isArray(payload.studies)) {
    throw new Error("Published Gallery index has an unsupported format.");
  }
  const studies = payload.studies as GalleryStudy[];
  studyDirectories.clear();
  for (const study of studies) {
    if (!directoryName.test(study.directory) || typeof study.snapshot?.id !== "string"
      || typeof study.snapshot.title !== "string" || typeof study.snapshot.summary !== "string"
      || !Array.isArray(study.lineageNodes) || !study.counts
      || Object.values(study.counts).some((count) => !Number.isSafeInteger(count) || count < 0)) {
      throw new Error("Published Gallery index contains an invalid study.");
    }
    studyDirectories.set(study.snapshot.id, study.directory);
  }
  return studies;
}

export async function loadPublishedSnapshot(study: GalleryStudy): Promise<GallerySnapshot> {
  if (!directoryName.test(study.directory)) throw new Error("Invalid published study directory.");
  const response = await fetch(publishedUrl(`snapshots/${study.directory}/snapshot.json`));
  if (!response.ok) throw new Error(`Published snapshot returned HTTP ${response.status}.`);
  const snapshot = parseGallerySnapshot(await response.json());
  if (snapshot.snapshot.id !== study.snapshot.id) throw new Error("Published snapshot ID does not match its index entry.");
  return snapshot;
}

export function publishedAssetUrl(snapshotId: string, assetPath: string): string | null {
  const directory = studyDirectories.get(snapshotId);
  if (!directory || !assetPath || assetPath.startsWith("/") || assetPath.split("/").some((part) => !part || part === "." || part === "..")
    || assetPath.includes("\\") || assetPath.includes("?")) return null;
  return publishedUrl(`snapshots/${directory}/${assetPath.split("/").map(encodeURIComponent).join("/")}`);
}
